import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';

/** Chart ranges shared by the Markets pages (no 1Y — per site spec). */
export const MARKET_RANGE_PRESETS = [
    { id: '1M', label: '1 month' },
    { id: '6M', label: '6 months' },
    { id: 'YTD', label: 'Year to date' },
    { id: '5Y', label: '5 years' },
    { id: 'ALL', label: 'All time' },
];

/** Fixed color per index key so toggling series never repaints the others. */
const INDEX_COLOR_ORDER = ['tsx', 'sp500', 'nasdaq', 'dow', 'russell2000', 'ftse', 'nikkei', 'dax'];
const INDEX_PALETTE = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'];

export function colorForIndexKey(key) {
    const i = INDEX_COLOR_ORDER.indexOf(key);
    return i >= 0 ? INDEX_PALETTE[i] : '#6b7280';
}

function parseISODate(s) {
    const [y, m, d] = String(s).split('-').map(Number);
    return new Date(y, m - 1, d);
}

function formatLegendDate(iso) {
    return parseISODate(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function formatPct(v) {
    const sign = v > 0 ? '+' : '';
    return `${sign}${v.toFixed(2)}%`;
}

const W = 880;
const H = 320;
const padL = 64;
const padR = 96; // room for direct end-labels
const padT = 24;
const padB = 48;
const Y_TICKS = 5;
const LABEL_GAP = 13;

/**
 * Multi-series comparison chart. Each series is rebased to percent change from
 * its first close inside the visible range, so indices of very different
 * magnitudes share one axis.
 *
 * @param {{ key: string, label: string, color: string, quotes: { date: string, closing: number }[] }[]} series
 *   quotes oldest-first, already filtered to the chart range
 */
export default function MarketComparisonChart({ series }) {
    const geom = useMemo(() => {
        const active = (series || []).filter(s => s.quotes && s.quotes.length > 0);
        if (!active.length) return null;

        const dateSet = new Set();
        for (const s of active) {
            for (const q of s.quotes) dateSet.add(q.date);
        }
        const dates = Array.from(dateSet).sort();
        const n = dates.length;
        const dateIdx = new Map(dates.map((d, i) => [d, i]));
        const iw = W - padL - padR;
        const ih = H - padT - padB;
        const xForIdx = i => padL + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);

        let minP = Infinity;
        let maxP = -Infinity;
        const rebased = active.map(s => {
            const base = Number(s.quotes[0].closing) || 1;
            const pts = s.quotes.map(q => {
                const pct = ((Number(q.closing) - base) / base) * 100;
                if (pct < minP) minP = pct;
                if (pct > maxP) maxP = pct;
                return { date: q.date, pct, xi: dateIdx.get(q.date) };
            });
            return { key: s.key, label: s.label, color: s.color, pts };
        });
        const span = maxP - minP || 1;
        const yForPct = pct => padT + ih - ((pct - minP) / span) * ih;

        const lines = rebased.map(s => {
            const coords = s.pts.map(p => ({ x: xForIdx(p.xi), y: yForPct(p.pct) }));
            const d = coords
                .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
                .join('');
            const pctByDate = new Map(s.pts.map(p => [p.date, p.pct]));
            const last = coords[coords.length - 1];
            return { key: s.key, label: s.label, color: s.color, d, pctByDate, endX: last.x, endY: last.y };
        });

        // Direct end-labels, nudged apart so they never overlap.
        const endLabels = lines
            .map(l => ({ key: l.key, label: l.label, color: l.color, x: l.endX, y: l.endY }))
            .sort((a, b) => a.y - b.y);
        for (let i = 1; i < endLabels.length; i++) {
            if (endLabels[i].y - endLabels[i - 1].y < LABEL_GAP) {
                endLabels[i].y = endLabels[i - 1].y + LABEL_GAP;
            }
        }
        for (let i = endLabels.length - 1; i >= 0; i--) {
            const maxY = H - padB - (endLabels.length - 1 - i) * LABEL_GAP;
            if (endLabels[i].y > maxY) endLabels[i].y = maxY;
        }

        const yTicks = [];
        for (let i = 0; i < Y_TICKS; i++) {
            const pct = minP + (span * i) / (Y_TICKS - 1);
            yTicks.push({ y: yForPct(pct), label: formatPct(pct) });
        }
        const zeroY = minP <= 0 && maxP >= 0 ? yForPct(0) : null;

        let xIndices;
        if (n <= 1) xIndices = [0];
        else if (n <= 8) xIndices = [0, n - 1];
        else xIndices = [0, Math.floor((n - 1) / 2), n - 1];
        const xMarks = xIndices.map(idx => ({ idx, x: xForIdx(idx), long: formatLegendDate(dates[idx]) }));

        return { dates, xForIdx, lines, endLabels, yTicks, zeroY, xMarks };
    }, [series]);

    const svgRef = useRef(null);
    const wrapRef = useRef(null);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        setHover(null);
    }, [series]);

    const updateHoverFromEvent = useCallback(
        e => {
            const svg = svgRef.current;
            const wrap = wrapRef.current;
            if (!svg || !wrap || !geom) return;
            const ctm = svg.getScreenCTM();
            if (!ctm) return;
            const pt = svg.createSVGPoint();
            pt.x = e.clientX;
            pt.y = e.clientY;
            const loc = pt.matrixTransform(ctm.inverse());
            const iw = W - padL - padR;
            const frac = iw > 0 ? (loc.x - padL) / iw : 0;
            const clamped = Math.max(0, Math.min(1, frac));
            const n = geom.dates.length;
            const idx = n <= 1 ? 0 : Math.round(clamped * (n - 1));

            const anchor = svg.createSVGPoint();
            anchor.x = geom.xForIdx(idx);
            anchor.y = padT;
            const screenPt = anchor.matrixTransform(ctm);
            const wrapRect = wrap.getBoundingClientRect();
            setHover({
                idx,
                tipLeft: screenPt.x - wrapRect.left,
                tipTop: screenPt.y - wrapRect.top,
            });
        },
        [geom],
    );

    const clearHover = useCallback(() => setHover(null), []);

    if (!geom) return null;

    const hoverDate = hover != null ? geom.dates[hover.idx] : null;
    const hoverRows = hoverDate
        ? geom.lines
            .filter(l => l.pctByDate.has(hoverDate))
            .map(l => ({ key: l.key, label: l.label, color: l.color, pct: l.pctByDate.get(hoverDate) }))
            .sort((a, b) => b.pct - a.pct)
        : [];

    const firstDate = geom.dates[0];
    const lastDate = geom.dates[geom.dates.length - 1];

    return (
        <div className="stock-price-chart-wrap" ref={wrapRef}>
            <span className="stock-chart-hover-aria" aria-live="polite">
                {hoverDate
                    ? `${formatLegendDate(hoverDate)}: ${hoverRows
                        .map(r => `${r.label} ${formatPct(r.pct)}`)
                        .join(', ')}`
                    : ''}
            </span>
            <svg
                ref={svgRef}
                className="stock-price-chart-svg"
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={`Percent change of ${geom.lines.map(l => l.label).join(', ')} from ${formatLegendDate(firstDate)} through ${formatLegendDate(lastDate)}`}
            >
                <text x={padL} y={14} className="stock-chart-axis-title">
                    Change since start of range (%)
                </text>

                <g className="stock-chart-grid" aria-hidden="true">
                    {geom.yTicks.map((t, yi) => (
                        <line
                            key={`grid-${yi}`}
                            x1={padL}
                            y1={t.y}
                            x2={W - padR}
                            y2={t.y}
                            className="stock-chart-grid-line"
                        />
                    ))}
                    {geom.zeroY != null && (
                        <line
                            x1={padL}
                            y1={geom.zeroY}
                            x2={W - padR}
                            y2={geom.zeroY}
                            className="markets-compare-zero-line"
                        />
                    )}
                </g>

                {geom.yTicks.map((t, yi) => (
                    <text
                        key={`ylabel-${yi}`}
                        x={padL - 8}
                        y={t.y}
                        textAnchor="end"
                        dominantBaseline="middle"
                        className="stock-chart-axis-tick stock-chart-axis-tick--value"
                    >
                        {t.label}
                    </text>
                ))}

                {geom.lines.map(l => (
                    <path
                        key={l.key}
                        d={l.d}
                        fill="none"
                        stroke={l.color}
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                ))}

                {geom.endLabels.map(l => (
                    <g key={`end-${l.key}`} aria-hidden="true">
                        <circle cx={l.x} cy={l.y} r={3} fill={l.color} />
                        <text
                            x={l.x + 7}
                            y={l.y}
                            dominantBaseline="middle"
                            className="markets-compare-end-label"
                        >
                            {l.label}
                        </text>
                    </g>
                ))}

                <g className="stock-chart-x-legend">
                    {geom.xMarks.map((m, i) => {
                        const isFirst = i === 0;
                        const isLast = i === geom.xMarks.length - 1;
                        const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                        const x = isFirst ? padL : isLast ? W - padR : m.x;
                        return (
                            <text
                                key={m.idx}
                                x={x}
                                y={H - 28}
                                textAnchor={anchor}
                                className="stock-chart-axis-tick stock-chart-axis-tick--date"
                            >
                                {m.long}
                            </text>
                        );
                    })}
                </g>

                <text x={W / 2} y={H - 8} textAnchor="middle" className="stock-chart-axis-footnote">
                    Date (horizontal) · % change from start of range (vertical)
                </text>

                <rect
                    className="stock-chart-interaction-layer"
                    x={padL}
                    y={padT}
                    width={W - padL - padR}
                    height={H - padT - padB}
                    fill="transparent"
                    cursor="crosshair"
                    onMouseMove={updateHoverFromEvent}
                    onMouseLeave={clearHover}
                />

                {hover != null && (
                    <g className="stock-chart-hover" aria-hidden="true">
                        <line
                            x1={geom.xForIdx(hover.idx)}
                            y1={padT}
                            x2={geom.xForIdx(hover.idx)}
                            y2={H - padB}
                            className="stock-chart-hover-line"
                        />
                    </g>
                )}
            </svg>
            {hover != null && hoverRows.length > 0 && (
                <div
                    className="stock-chart-tooltip markets-compare-tooltip"
                    style={{ left: hover.tipLeft, top: hover.tipTop }}
                >
                    <div className="stock-chart-tooltip-date">{formatLegendDate(hoverDate)}</div>
                    {hoverRows.map(r => (
                        <div key={r.key} className="markets-compare-tooltip-row">
                            <span
                                className="markets-compare-swatch"
                                style={{ backgroundColor: r.color }}
                                aria-hidden="true"
                            />
                            <span className="markets-compare-tooltip-label">{r.label}</span>
                            <span className="markets-compare-tooltip-value">{formatPct(r.pct)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
